import React, { useState, useEffect } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { Select } from "../../components/ui/Select";
import { EntitySearchPicker } from "../../components/ui/EntitySearchPicker";
import { reportsApi } from "../../utils/api";
import { formatDateTime } from "../../utils/helpers";
import {
  ENTITY_TYPE_META,
  REASON_LABELS,
  createReasonOptions,
  entityTypeSelectOptions,
} from "./constants";

const DEFAULT_REASON = "incorrect_info";

/**
 * The form body. Lives below ModalContent's Dialog.Portal, which unmounts on
 * close — so every open starts from fresh state with no reset effect, and an
 * in-flight lookup is torn down when the admin closes the modal.
 */
function CreateReportForm({ onCancel, onCreated, submitting, setSubmitting }) {
  const [entityType, setEntityType] = useState("event");
  // { id, label } once picked, else null. A null `label` means the admin pasted
  // an ID we never resolved to a name.
  const [entity, setEntity] = useState(null);
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState(null);
  const [existingReports, setExistingReports] = useState(null);

  const entityId = entity?.id;

  // Informational only: show what's already been reported on this entity so the
  // admin can decide. Never blocks — user reports have no dedupe by design.
  useEffect(() => {
    if (!entityId) {
      setExistingReports(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await reportsApi.getAll({
          entity_id: entityId,
          page: 1,
          page_size: 5,
        });
        if (!cancelled) setExistingReports(data);
      } catch {
        if (!cancelled) setExistingReports(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  const handleEntityTypeChange = (value) => {
    setEntityType(value);
    // The picker resets itself via `key`, but the confirmed selection above it
    // is this component's state — an artist id left attached to a venue report
    // would 404 on submit.
    setEntity(null);
  };

  const handleSubmit = async () => {
    if (!entityId || !reason) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await reportsApi.create({
        entity_type: entityType,
        entity_id: entityId,
        reason,
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      onCreated();
    } catch (err) {
      console.error("Error creating report:", err);
      setFormError(err.response?.data?.detail || "Could not create the report");
    } finally {
      setSubmitting(false);
    }
  };

  const existingTotal = existingReports?.pagination?.total || 0;

  return (
    <div className="space-y-4">
      <Select
        id="new_report_entity_type"
        label="Entity type"
        required
        value={entityType}
        onChange={(e) => handleEntityTypeChange(e.target.value)}
        options={entityTypeSelectOptions}
      />

      <EntitySearchPicker
        key={entityType}
        id="new_report_entity_search"
        label={`${ENTITY_TYPE_META[entityType].label} to report`}
        required
        value={entity}
        onChange={setEntity}
        disabled={submitting}
        search={(query, config) =>
          ENTITY_TYPE_META[entityType]
            .search(query, config)
            .then((data) => data?.[ENTITY_TYPE_META[entityType].listKey] || [])
        }
        optionLabel={ENTITY_TYPE_META[entityType].optionLabel}
        optionSublabel={ENTITY_TYPE_META[entityType].optionSublabel}
      />

      {existingTotal > 0 && (
        <div className="rounded-md border border-yellow-500/20 bg-yellow-500/10 p-3 text-sm text-yellow-700">
          <p className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" />
            This entity already has {existingTotal} report
            {existingTotal === 1 ? "" : "s"}
          </p>
          <ul className="mt-2 space-y-1">
            {(existingReports?.reports || []).map((r) => (
              <li key={r.id}>
                {REASON_LABELS[r.reason] || r.reason} · {r.status} ·{" "}
                {formatDateTime(r.created_at)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Select
        id="new_report_reason"
        label="Reason"
        required
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        options={createReasonOptions}
      />

      <div>
        <label
          htmlFor="new_report_description"
          className="mb-1 block text-sm font-medium"
        >
          Description
        </label>
        <textarea
          id="new_report_description"
          rows={3}
          maxLength={2000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is wrong with this entity?"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {formError && (
        <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-700">
          {formError}
        </div>
      )}

      <ModalFooter>
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting || !entityId || !reason}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create report
        </Button>
      </ModalFooter>
    </div>
  );
}

/**
 * File a report as an admin. Posts to the consumer POST /reports (see the comment
 * on reportsApi.create) — the report is attributed to the acting admin.
 *
 * Errors render inside the modal rather than through the page's banners: the
 * Radix overlay is z-50 over the page, so a banner set while this is open would
 * be invisible. Success is the page's job, after this closes.
 *
 * `submitting` lives here rather than in the form because ModalContent needs it
 * for `closeDisabled`; everything else is form state and resets by unmounting.
 */
export default function CreateReportModal({ open, onOpenChange, onCreated }) {
  const [submitting, setSubmitting] = useState(false);

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent title="New report" closeDisabled={submitting}>
        <CreateReportForm
          onCancel={() => onOpenChange(false)}
          onCreated={onCreated}
          submitting={submitting}
          setSubmitting={setSubmitting}
        />
      </ModalContent>
    </Modal>
  );
}
